import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { sanitizeClassicHostedSearchPageName, sanitizeHostedSearchPageId, sanitizeOrganizationId } from './Sanitization.js';
import { LaunchDarklyConstant } from './constants/LaunchDarklyConstant.js';
import { URLConstant } from './constants/URLConstant.js';
import { ApplicationHelper } from './helpers/ApplicationHelper.js';
import { RegionHelper } from './helpers/RegionHelper.js';
import { URLHelper } from './helpers/URLHelper.js';
import { ENVIRONMENT } from './types/EnvironmentType.js';
var HELP_WIDGET_ID = '3b5b3092-300e-45fd-80a4-35831bcbe0d5';
var HELP_WIDGET_ORG = 'coveosearch';
var origin = "".concat(window.location.protocol, "//").concat(window.location.hostname).concat(window.location.port ? ':' + window.location.port : '');
var region = RegionHelper.extractRegion(origin);
var dockerEnv = '$$_ENV_$$';
var env = dockerEnv.indexOf('$') === -1 ? dockerEnv : URLHelper.getEnvFromDocumentURL(origin, region);
var ENVIRONMENT_SHORT = {
    production: 'prd',
    hipaa: 'hip'
};
var envShort = ENVIRONMENT_SHORT[env] || env;
var platformUrl = URLHelper.getServiceUrl(env, region, 'platform', env === ENVIRONMENT.local ? origin : URLConstant.APPLICATION_PLATFORM_BASE_URL[env]);
var commerceHubUrl = URLHelper.getServiceUrl(env, region, 'commerce', URLConstant.APPLICATION_COMMERCE_HUB_BASE_URL[env]);
var knowledgeHubUrl = URLHelper.getServiceUrl(env, region, 'knowledge', URLConstant.APPLICATION_KNOWLEDGE_HUB_BASE_URL[env]);
var searchUrl = URLHelper.getServiceUrl(env, region, 'search', env === ENVIRONMENT.local ? origin : URLConstant.CLOUD_SEARCH_BASE_URL[env]);
var uaAlts = URLHelper.getServiceUrl(env, region, 'uaalts', []);
if (platformUrl === RegionHelper.injectRegion(URLConstant.APPLICATION_PLATFORM_BASE_URL[env], region) && uaAlts.length === 0) {
    uaAlts = URLConstant.USAGE_ANALYTICS_ALT_DOMAINS[env].map(function(url) {
        return RegionHelper.injectRegion(url, region);
    });
}
var ApplicationBaseUrl = URLHelper.getApplicationBaseUrls(env);
var _obj;
export var Config = {
    app: {
        clientId: ApplicationHelper.getCurrentApplicationClientId(),
        name: ApplicationHelper.getCurrentApplicationName(),
        nameLocalized: ApplicationHelper.getCurrentApplicationNameLocalized()
    },
    env: env,
    envShort: envShort,
    CloudPlatform: {
        responseType: 'code',
        url: platformUrl,
        commerceHubUrl: commerceHubUrl,
        knowledgeHubUrl: knowledgeHubUrl,
        region: region,
        authorizePath: '/oauth/authorize',
        tokenPath: '/oauth/token',
        getUrlForRegion: function getUrlForRegion(newRegion) {
            var url = RegionHelper.injectRegion(ApplicationBaseUrl[ApplicationHelper.getCurrentApplicationName()], newRegion);
            // Return only the origin (scheme + host) since callers append window.location.pathname
            return new URL(url).origin;
        },
        getOrganizationEndpoints: function getOrganizationEndpoints(orgId) {
            // Find the right suffix to use for the current environment
            var envSuffix = Config.env || ENVIRONMENT.dev;
            if (envSuffix === ENVIRONMENT.local) {
                envSuffix = ENVIRONMENT.dev;
            } else if (envSuffix === ENVIRONMENT.production) {
                envSuffix = '';
            }
            return {
                admin: orgId ? "https://".concat(orgId, ".admin.org").concat(envSuffix, ".coveo.com") : Config.CloudPlatform.url,
                search: orgId ? "https://".concat(orgId, ".org").concat(envSuffix, ".coveo.com/rest/search") : Config.SearchApi.url,
                platform: orgId ? "https://".concat(orgId, ".org").concat(envSuffix, ".coveo.com") : Config.CloudPlatform.url,
                analytics: orgId ? "https://".concat(orgId, ".analytics.org").concat(envSuffix, ".coveo.com/rest/ua") : Config.UsageAnalytics.url
            };
        }
    },
    CloudPlatformDocumentation: {
        url: platformUrl + '/docs',
        helpWidget: "https://".concat(HELP_WIDGET_ORG, ".admin.org.coveo.com/rest/organizations/").concat(HELP_WIDGET_ORG, "/ipxinterface/v1/interfaces/").concat(HELP_WIDGET_ID, "/loader"),
        helpWidgetApiKey: 'xx6a436255-bd33-4a4b-916d-851d37ae0347'
    },
    SearchApi: {
        url: "".concat(URLHelper.getServiceUrl(env, region, 'search-api', platformUrl), "/rest/search")
    },
    Search: {
        url: searchUrl,
        getSearchPageViewUrl: function getSearchPageViewUrl(param) {
            var orgId = param.orgId, id = param.id;
            return "".concat(searchUrl, "/rest/organizations/").concat(sanitizeOrganizationId(orgId), "/searchinterfaces/").concat(sanitizeHostedSearchPageId(id), "/html");
        },
        getClassicSearchPageViewUrl: function getClassicSearchPageViewUrl(param) {
            var orgId = param.orgId, name = param.name;
            return "".concat(searchUrl, "/pages/").concat(sanitizeOrganizationId(orgId), "/").concat(sanitizeClassicHostedSearchPageName(name));
        },
        getInProductExperienceEditUrl: function getInProductExperienceEditUrl(param) {
            var orgId = param.orgId, id = param.id;
            return "".concat(searchUrl, "/rest/organizations/").concat(sanitizeOrganizationId(orgId), "/ipxinterface/v1/interfaces/").concat(sanitizeHostedSearchPageId(id), "/edit");
        }
    },
    UsageAnalytics: {
        url: URLHelper.getServiceUrl(env, region, 'ua', platformUrl) + '/rest/ua/v15',
        alts: (Array.isArray(uaAlts) ? uaAlts : [
            uaAlts
        ]).map(function(url) {
            return "".concat(url, "/rest/ua/v15");
        }),
        envEnabled: (_obj = {}, _define_property(_obj, ENVIRONMENT.local, false), _define_property(_obj, ENVIRONMENT.dev, true), _define_property(_obj, ENVIRONMENT.stg, true), _define_property(_obj, ENVIRONMENT.production, true), _define_property(_obj, ENVIRONMENT.hipaa, true), _obj)
    },
    LogBrowser: {
        url: URLHelper.getServiceUrl(env, region, 'log-browser', URLConstant.API_ENDPOINT_URL[env])
    },
    SourcesPush: {
        url: "".concat(URLHelper.getServiceUrl(env, region, 'push', URLConstant.API_ENDPOINT_URL[env]), "/push/v1")
    },
    OnlineHelp: {
        url: URLHelper.getServiceUrl(env, region, 'onlinehelp', URLConstant.ONLINE_HELP_BASE_URL[env])
    },
    StatusPage: {
        url: URLConstant.STATUS_PAGE_URL[env],
        urlApi: URLConstant.STATUS_PAGE_API_URL[env]
    },
    LaunchDarkly: {
        Platform: {
            clientID: LaunchDarklyConstant.PLATFORM_CLIENT_ID[env]
        },
        KnowledgeUI: {
            clientID: LaunchDarklyConstant.KNOWLEDGE_UI_CLIENT_ID[env]
        },
        CustomerService: {
            clientID: LaunchDarklyConstant.CUSTOMER_SERVICE_CLIENT_ID[env]
        },
        SearchInterface: {
            clientID: LaunchDarklyConstant.SEARCH_INTERFACE_CLIENT_ID[env]
        },
        CommerceService: {
            clientID: LaunchDarklyConstant.COMMERCE_SERVICE_CLIENT_ID[env]
        }
    }
};

//# sourceMappingURL=Config.js.map